import React, { useState, useEffect, useRef } from 'react';
import Window from './Window';
import DesktopIcon from './DesktopIcon';
import ContextMenu from '../ContextMenu/ContextMenu';
import Settings from '../Settings/Settings';
import FileExplorer from '../FileExplorer/FileExplorer';
import Terminal from '../Terminal/Terminal';
import AIAssistant from '../AIAssistant/AIAssistant';
import YouTube from '../YouTube/YouTube';
import Notepad from '../Notepad/Notepad';
import Calculator from '../Calculator/Calculator';
import Store from '../Store/Store';
import Clock from '../Clock/Clock';

import CodeEditor from '../CodeEditor/CodeEditor';

const Desktop = ({ theme, openApps, closeApp, launchApp, addAppToDesktop, isMobile, currentWallpaper, changeWallpaper }) => {
  // Adding a subtle parallax effect to wallpaper
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [timeOfDay, setTimeOfDay] = useState('');
  const [temperature, setTemperature] = useState('30 °C');
  const [weatherCondition, setWeatherCondition] = useState('Clear');
  const [contextMenu, setContextMenu] = useState(null);
  const [desktopView, setDesktopView] = useState('largeIcons');
  const [sortOrder, setSortOrder] = useState('name');

  // Define default desktop icons and their properties - only essential apps for new users
  const defaultIcons = [
    { id: 'terminal', name: 'Terminal', icon: 'terminal', component: 'Terminal' },
    { id: 'file-explorer', name: 'This PC', icon: 'folder', component: 'FileExplorer' },
    { id: 'browser', name: 'Web Browser', icon: 'globe', component: 'WebBrowser' },
    { id: 'store', name: 'Store', icon: 'store', component: 'Store' },
    { id: 'ai-assistant', name: 'Kiwi AI', icon: 'robot', component: 'AIAssistant' },
    // Add more default icons as needed
  ];

  // Force reset localStorage to default icons
  useEffect(() => {
    // Reset desktop icons to defaults to apply new icon names
    localStorage.setItem('webos-desktop-icons', JSON.stringify(defaultIcons));
    console.log("Desktop icons reset to defaults");
  }, []);

  // Load desktop icons from localStorage or use defaults
  const [desktopIcons, setDesktopIcons] = useState(() => {
    try {
      const savedIcons = localStorage.getItem('webos-desktop-icons');
      return savedIcons ? JSON.parse(savedIcons) : defaultIcons;
    } catch (e) {
      console.error('Failed to load desktop icons:', e);
      return defaultIcons;
    }
  });

  // Save desktop icons to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('webos-desktop-icons', JSON.stringify(desktopIcons));
  }, [desktopIcons]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      // Create a subtle parallax effect
      setMousePosition({
        x: e.clientX / window.innerWidth - 0.5,
        y: e.clientY / window.innerHeight - 0.5
      });
    };

    // Set time of day for greeting
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      setTimeOfDay('morning');
    } else if (hour >= 12 && hour < 18) {
      setTimeOfDay('afternoon');
    } else {
      setTimeOfDay('evening');
    }

    window.addEventListener('mousemove', handleMouseMove);
    
    // Add a global click event listener to close context menu when clicking anywhere else
    const handleGlobalClick = (e) => {
      // Close context menu on any click (including right-click) if it's open
      if (contextMenu) {
        // Check if click is outside the context menu
        const contextMenuElement = document.querySelector('.context-menu');
        if (!contextMenuElement || !contextMenuElement.contains(e.target)) {
          setContextMenu(null);
        }
      }
    };
    
    // Add global function to allow adding apps to desktop from the Store component
    window.addAppToDesktop = (app) => {
      // Check if app is already on desktop
      setDesktopIcons(prevIcons => {
        if (prevIcons.some(icon => icon.id === app.id)) {
          console.log('App already on desktop:', app.id);
          return prevIcons;
        }
        
        // Store installed app in localStorage for tracking in Store
        const installedApps = JSON.parse(localStorage.getItem('webos-installed-apps') || '{}');
        installedApps[app.id] = true;
        localStorage.setItem('webos-installed-apps', JSON.stringify(installedApps));
        
        // Add new app to desktop icons
        console.log('Adding app to desktop icons:', app);
        return [...prevIcons, app];
      });
    };
    
    // Listen for app launch requests from Store
    const handleAppLaunch = (e) => {
      const { appId } = e.detail;
      console.log('Launching app:', appId);
      
      // Find the app in desktop icons
      const app = desktopIcons.find(icon => icon.id === appId);
      if (app) {
        launchApp(app);
      }
    };
    
    window.addEventListener('click', handleGlobalClick);
    window.addEventListener('mousedown', handleGlobalClick);
    window.addEventListener('contextmenu', handleGlobalClick);
    window.addEventListener('webos-launch-app', handleAppLaunch);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleGlobalClick);
      window.removeEventListener('mousedown', handleGlobalClick);
      window.removeEventListener('contextmenu', handleGlobalClick);
      window.removeEventListener('webos-launch-app', handleAppLaunch);
      delete window.addAppToDesktop;
    };
  }, [desktopIcons, launchApp]);

  // Handle right-click on desktop background
  const handleDesktopContextMenu = (e) => {
    e.preventDefault(); // This prevents the browser's default context menu
    e.stopPropagation(); // Stop event from bubbling to parent elements
    
    // Only show context menu if clicked on desktop background
    const clickedOnIcon = e.target.closest('.desktop-icon');
    const clickedOnApp = e.target.closest('.window');
    
    if (!clickedOnIcon && !clickedOnApp) {
      // Right-clicked on desktop background
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        type: 'desktop',
        data: { desktopView, sortOrder },
        actions: desktopActions // Add the desktop actions to the context menu
      });
    }
  };

  // Context menu actions for desktop
  const desktopActions = {
    refresh: () => {
      // Simulate refresh
      const refreshAnimation = document.createElement('div');
      refreshAnimation.style.position = 'fixed';
      refreshAnimation.style.top = '0';
      refreshAnimation.style.left = '0';
      refreshAnimation.style.width = '100%';
      refreshAnimation.style.height = '100%';
      refreshAnimation.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
      refreshAnimation.style.zIndex = '9999';
      refreshAnimation.style.transition = 'opacity 0.5s ease';
      document.body.appendChild(refreshAnimation);
      
      setTimeout(() => {
        refreshAnimation.style.opacity = '0';
        setTimeout(() => {
          document.body.removeChild(refreshAnimation);
        }, 500);
      }, 100);
      
      setContextMenu(null);
    },
    viewLarge: () => {
      setDesktopView('largeIcons');
      setContextMenu(null);
    },
    viewSmall: () => {
      setDesktopView('smallIcons');
      setContextMenu(null);
    },
    sortByName: () => {
      setSortOrder('name');
      setContextMenu(null);
    },
    arrangeWindows: () => {
      arrangeAllWindows();
      setContextMenu(null);
    },
    openSettings: (section) => {
      const settingsApp = {
        id: 'settings-' + Date.now(),
        name: 'Settings',
        component: 'Settings',
        initialSection: section, // Pass the section to open
        icon: 'cog',
        onWallpaperChange: changeWallpaper // Pass the wallpaper change function
      };
      launchApp(settingsApp);
      setContextMenu(null);
    }
  };

  // Window actions for context menu
  const windowActions = {
    closeWindow: (windowId) => {
      // Find the app with this id and close it
      const app = openApps.find(app => app.id === windowId);
      if (app) {
        closeApp(app.id);
      }
      setContextMenu(null);
    }
  };

  // File actions
  const fileActions = {
    openFile: (file) => {
      launchApp(file);
      setContextMenu(null);
    }
  };

  // Function to add a desktop icon if it doesn't already exist
  const addDesktopIcon = (icon) => {
    console.log('Adding desktop icon:', icon);
    
    // Check if this icon already exists in the desktop icons
    const iconExists = desktopIcons.some(existingIcon => existingIcon.id === icon.id);
    
    if (!iconExists) {
      // Create a new icon with the correct structure
      const newIcon = {
        id: icon.id,
        name: icon.name,
        icon: icon.icon || 'question', // Default icon if none provided
        component: icon.component,
        url: icon.url
      };
      
      console.log('New desktop icon created:', newIcon);
      
      // Create updated array with the new icon
      const updatedIcons = [...desktopIcons, newIcon];
      
      // Update state and save to localStorage
      setDesktopIcons(updatedIcons);
      localStorage.setItem('webos-desktop-icons', JSON.stringify(updatedIcons));
      
      // Show a notification that the app was added
      if (window.showNotification) {
        window.showNotification({
          title: 'App Installed',
          message: `${icon.name} has been added to your desktop.`,
          type: 'success',
          duration: 3000
        });
      }
      
      return true;
    }
    
    return false;
  };
  
  // Register the addDesktopIcon function globally and connect it to addAppToDesktop
  useEffect(() => {
    window.addDesktopIcon = addDesktopIcon;
    
    // If addAppToDesktop is provided, wrap it with our addDesktopIcon function
    if (addAppToDesktop) {
      console.log('Connecting addAppToDesktop with addDesktopIcon');
    }
    
    return () => {
      // Cleanup when component unmounts
      window.addDesktopIcon = undefined;
    };
  }, [desktopIcons, addAppToDesktop]);

  // Add global window arrangement functionality
  const arrangeAllWindows = () => {
    // Find all windows
    const windows = document.querySelectorAll('.window');
    const numWindows = windows.length;
    
    if (numWindows <= 1) return; // No need to arrange a single window
    
    // Calculate grid dimensions based on window count
    const cols = Math.ceil(Math.sqrt(numWindows));
    const rows = Math.ceil(numWindows / cols);
    
    // Calculate window size based on available space
    const workAreaWidth = window.innerWidth - 40; // 20px margin on each side
    const workAreaHeight = window.innerHeight - 100; // account for taskbar and margins
    
    const windowWidth = Math.floor(workAreaWidth / cols) - 20;
    const windowHeight = Math.floor(workAreaHeight / rows) - 20;
    
    windows.forEach((win, index) => {
      // Calculate grid position
      const col = index % cols;
      const row = Math.floor(index / cols);
      
      // Calculate window position
      const left = 20 + (col * (windowWidth + 20));
      const top = 20 + (row * (windowHeight + 20));
      
      // Update window
      win.style.width = `${windowWidth}px`;
      win.style.height = `${windowHeight}px`;
      win.style.left = `${left}px`;
      win.style.top = `${top}px`;
      win.style.zIndex = 1000 + index;
    });
  };
  
  // Add hotkey for window arrangement (Windows+W)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Windows key (Meta) + W
      if ((e.metaKey || e.ctrlKey) && e.key === 'w') {
        e.preventDefault();
        arrangeAllWindows();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    // Make function available globally
    window.arrangeAllWindows = arrangeAllWindows;
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      delete window.arrangeAllWindows;
    };
  }, []);

  // Tracking selected icon for visual feedback
  const [selectedIcon, setSelectedIcon] = useState(null);
  
  // Handle icon click - launches the app
  const handleIconClick = (app) => {
    launchApp(app);
    setSelectedIcon(app.id);
    // Clear selection after a delay
    setTimeout(() => setSelectedIcon(null), 300);
  };
  
  // Handle right-click on an icon
  const handleIconContextMenu = (e, app) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Set custom context menu for icon
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      actions: {
        open: () => {
          launchApp(app);
          setContextMenu(null);
        },
        delete: () => {
          // Remove icon from desktop
          setDesktopIcons(prev => prev.filter(icon => icon.id !== app.id));
          // Save updated icons to localStorage
          localStorage.setItem('webos-desktop-icons', JSON.stringify(
            desktopIcons.filter(icon => icon.id !== app.id)
          ));
          setContextMenu(null);
        }
      }
    });
  };
  
  // Format menu item text for better display
  const formatMenuItemText = (key) => {
    return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
  };
  
  // Get icon background color based on app ID
  const getIconBackgroundColor = (id) => {
    // Generate a consistent color based on the app ID
    const colors = [
      { primary: '#4a90e2', secondary: '#2e5bea' },
      { primary: '#50e3c2', secondary: '#24c1a1' },
      { primary: '#b8e986', secondary: '#7ed321' },
      { primary: '#ffd058', secondary: '#f8a623' },
      { primary: '#ff9500', secondary: '#e27c02' },
      { primary: '#ff5e3a', secondary: '#e73827' },
      { primary: '#ef5350', secondary: '#b61827' },
      { primary: '#ec407a', secondary: '#b4004e' },
      { primary: '#ab47bc', secondary: '#7b1fa2' },
      { primary: '#7e57c2', secondary: '#4d2c91' }
    ];
    
    // Use hash function to get consistent index
    const hashCode = id.split('').reduce(
      (prevHash, currVal) => (((prevHash << 5) - prevHash) + currVal.charCodeAt(0)) | 0, 0
    );
    const colorIndex = Math.abs(hashCode % colors.length);
    
    return colors[colorIndex];
  };
  
  // Helper function to get icon class name
  const getIconClassName = (iconName) => {
    const brandIcons = ['chrome', 'edge', 'spotify', 'facebook', 'twitter', 'windows', 'youtube'];
    if (brandIcons.includes(iconName)) {
      return `fab fa-${iconName}`;
    }
    return `fas fa-${iconName}`;
  };
  
  // Calculate window position for new windows
  const calculateWindowPosition = (index) => {
    const offset = 30 + (index * 25) % 150;
    return { 
      x: offset, 
      y: offset
    };
  };
  
  // Calculate window size based on app type
  const calculateWindowSize = (app) => {
    // Default sizes for different app types
    switch(app.component) {
      case 'Terminal':
        return { width: 700, height: 500 };
      case 'FileExplorer':
        return { width: 800, height: 600 };
      case 'YouTube':
        return { width: 900, height: 650 };
      case 'WebBrowser':
        return { width: 1000, height: 700 };
      case 'CodeEditor':
        return { width: 1000, height: 700 };
      default:
        return { width: 800, height: 600 };
    }
  };
  
  // Track window z-indices for proper stacking
  const [windowZIndices, setWindowZIndices] = useState({});
  
  // Function to bring a window to the front
  const bringWindowToFront = (windowId) => {
    setWindowZIndices(prev => {
      // Find the highest current z-index
      const highestZ = Object.values(prev).reduce(
        (max, z) => Math.max(max, z), 0
      );
      
      // Set this window's z-index higher than all others
      return {
        ...prev,
        [windowId]: highestZ + 1
      };
    });
  };

  // Add a keyframes style element to the component for custom animations
  useEffect(() => {
    // Create style element for desktop animations
    const desktopStyles = document.createElement('style');
    desktopStyles.textContent = `
      @keyframes floatUp {
        0% { transform: translateY(20px); opacity: 0; }
        100% { transform: translateY(0); opacity: 1; }
      }
      
      @keyframes backgroundPulse {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      
      .desktop-container {
        background-size: 200% 200%;
        animation: backgroundPulse 30s ease infinite;
      }
      
      .desktop-appear {
        animation: floatUp 0.5s ease;
      }
      
      .desktop-icon-grid {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      .icon-drag-over {
        background-color: rgba(255, 255, 255, 0.05);
        transform: scale(1.03);
      }
    `;
    
    document.head.appendChild(desktopStyles);
    
    // Apply floatUp animation to desktop icons with a staggered delay
    const icons = document.querySelectorAll('.desktop-icon');
    icons.forEach((icon, index) => {
      icon.style.animation = `floatUp 0.5s ease ${index * 0.05}s backwards`;
    });
    
    return () => {
      document.head.removeChild(desktopStyles);
    };
  }, []);

  // Fix the renderComponent function to properly handle WebBrowser
  const renderComponent = (component, app) => {
    switch (component) {
      case 'FileExplorer':
        return <FileExplorer theme={theme} />;
        
      case 'Terminal':
        return <Terminal theme={theme} />;
      
      case 'AIAssistant':
        return <AIAssistant theme={theme} />;

      case 'YouTube':
        return <YouTube theme={theme} url={app.url} />;

      case 'Notepad':
        return <Notepad theme={theme} initialContent={app.content || ""} />;
        
      case 'WebBrowser':
        // The WebBrowser component is defined inside Window.js, 
        // so we'll let the Window component handle rendering it
        return null;
        
      case 'Calculator':
        return <Calculator theme={theme} />;
        
      case 'Store':
        return <Store theme={theme} />;
        
      case 'Clock':
        return <Clock theme={theme} />;
        

        
      case 'CodeEditor':
        return <CodeEditor theme={theme} />;
      
      case 'Settings':
        return <Settings 
                theme={theme} 
                onThemeChange={(newTheme) => {
                  // This simulates changing the theme through the window
                  document.body.className = `theme-${newTheme}`;
                  // If your app has a real theme changing function, call it here
                }}
                onWallpaperChange={changeWallpaper}
                initialSection={app.initialSection || 'display'}
              />;
      
      default:
        return <div>App not found: {component}</div>;
    }
  };

  return (
    <div 
      className="desktop"
      onClick={(e) => e.stopPropagation()}
      onContextMenu={handleDesktopContextMenu}
      style={{ 
        height: '100vh', 
        width: '100vw',
        position: 'relative',
        overflowX: 'hidden',
        overflowY: isMobile ? 'auto' : 'hidden',
        padding: isMobile ? '60px 10px 60px 10px' : '10px',
        boxSizing: 'border-box' 
      }}
    >
      {/* Weather Widget - always visible */}
      <div 
        className="weather-widget glass-effect"
        style={{
          position: 'fixed',
          bottom: '10px',
          left: '140px',
          padding: '10px 15px',
          borderRadius: '24px',
          color: 'white',
          backgroundColor: 'rgba(20, 20, 40, 0.6)',
          backdropFilter: 'blur(30px)',
          width: '200px',
          height: '48px',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          zIndex: '1000',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ 
            fontSize: '24px', 
            color: 'yellow', 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '15px'
          }}>
            <i className="fas fa-sun"></i>
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 'bold', lineHeight: '1.2' }}>
              {temperature}
            </div>
            <div style={{ fontSize: '12px', opacity: '0.8', lineHeight: '1.2' }}>
              Weather {weatherCondition}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Icons */}
      <div 
        className="desktop-icons-container"
        style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, 110px)',
          gridTemplateRows: 'repeat(6, minmax(110px, 130px))',
          gridAutoFlow: 'column',
          gap: '15px',
          justifyContent: 'start',
          alignContent: 'start',
          padding: '25px',
          height: '100%',
          maxWidth: '100%',
          overflow: 'auto'
        }}
      >
        {desktopIcons.map((icon, index) => {
          const colors = getIconBackgroundColor(icon.id);
          return (
          <div
            key={icon.id}
            className="desktop-icon"
            onClick={() => handleIconClick(icon)}
            onDoubleClick={() => handleIconClick(icon)}
            onContextMenu={(e) => handleIconContextMenu(e, icon)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100px',
              height: '120px',
              padding: '10px',
              cursor: 'pointer',
              borderRadius: '16px',
              transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              backgroundColor: selectedIcon === icon.id 
                ? 'rgba(255, 255, 255, 0.15)' 
                : 'transparent',
              transform: selectedIcon === icon.id ? 'scale(1.05) translateY(-5px)' : 'scale(1)',
              border: selectedIcon === icon.id ? '1px solid rgba(255, 255, 255, 0.12)' : 'none',
              overflow: 'hidden',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05) translateY(-5px)';
              e.currentTarget.style.backgroundColor = 'rgba(20, 30, 60, 0.2)';
            }}
            onMouseLeave={(e) => {
              if (selectedIcon !== icon.id) {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >

            
            <div 
              style={{ 
                width: isMobile ? '50px' : '60px', 
                height: isMobile ? '50px' : '60px',
                borderRadius: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '22px' : '26px',
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                color: 'white',
                marginBottom: '12px',
                boxShadow: `0 8px 20px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(255, 255, 255, 0.2), 0 0 10px rgba(${parseInt(colors.primary.slice(1, 3), 16)}, ${parseInt(colors.primary.slice(3, 5), 16)}, ${parseInt(colors.primary.slice(5, 7), 16)}, 0.5)`,
                transition: 'all 0.3s ease',
                transform: 'translateZ(0)',
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden',
                position: 'relative',
                zIndex: 2,
                overflow: 'hidden'
              }}
            >
              {/* Inner highlight effect */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '30%',
                borderRadius: '18px 18px 50% 50%',
                background: 'linear-gradient(to bottom, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%)',
                opacity: 0.6,
                zIndex: 1,
                pointerEvents: 'none'
              }}></div>
              
              <i className={icon.className ? `${icon.className} fa-${icon.icon}` : getIconClassName(icon.icon)} style={{ position: 'relative', zIndex: 2 }}></i>
            </div>
            
            <div 
              className="desktop-icon-text"
              style={{ 
                fontSize: isMobile ? '0.75rem' : '0.85rem',
                fontWeight: '500',
                color: 'white',
                wordBreak: 'break-word',
                textAlign: 'center',
                maxWidth: '90px',
                textShadow: '0 2px 5px rgba(0, 0, 0, 0.5)',
                lineHeight: '1.2',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                display: '-webkit-box',
                overflow: 'hidden',
                letterSpacing: '0.3px',
                zIndex: 2,
                position: 'relative'
              }}
            >
              {icon.name || icon.title}
            </div>
            

          </div>
        )})}
      </div>

      {/* Open Windows */}
      {openApps.map((app, index) => {
        // On mobile, only show the most recent window
        if (isMobile && index !== openApps.length - 1) {
          return null;
        }
        
        return (
          <Window
            key={`window-${app.id}`}
            app={app}
            closeApp={closeApp}
            defaultPosition={calculateWindowPosition(index)}
            defaultSize={calculateWindowSize(app)}
            theme={theme}
            bringToFront={bringWindowToFront}
            zIndex={windowZIndices[app.id] || 1}
            isMobile={isMobile}
          />
        );
      })}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            backgroundColor: theme === 'dark' ? 'rgba(40, 44, 52, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            borderRadius: '8px',
            padding: '4px 0',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            width: isMobile ? '200px' : '220px'
          }}
        >
          {/* Menu Items */}
          {contextMenu.actions && Object.entries(contextMenu.actions).map(([key, action]) => (
            <div
              key={key}
              onClick={(e) => {
                e.stopPropagation();
                action();
              }}
              style={{
                padding: isMobile ? '8px 12px' : '10px 15px',
                cursor: 'pointer',
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                transition: 'background-color 0.2s',
                hover: {
                  backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                },
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              {formatMenuItemText(key)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Desktop; 